import { RefObject, useRef } from 'react';

import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Section } from '@/components/shared/layout/section';
import { formatPrice } from '@/components/shared/utility/format';
import { styled } from '@/stitches.config';
import type { PartsTireTableProps } from '@/types/shared/parts';

const Table = styled('div', {
  maxWidth: '100%',
  overflowX: 'auto',
  scrollbarWidth: 'none', // For Firefox
  '-ms-overflow-style': 'none', // For Internet Explorer and Edge Legacy
  '&::-webkit-scrollbar': {
    display: 'none', // For all modern browsers (Chrome, Safari, Edge)
  },
  '& table': {
    borderCollapse: 'collapse',
    borderSpacing: 0,
    paddingBottom: '$space-px',
    '& .hidden': {
      opacity: 0,
      '& th': {
        height: 0,
        border: 'unset',
        padding: '$space-0',
      },
    },
    '& th, & td': {
      textAlign: 'center',
      verticalAlign: 'middle',
      paddingTop: 14,
      paddingBottom: '$space-3',
      px: '$space-4',
      minWidth: '$size-40',
      span: {
        wordBreak: 'break-word',
      },
    },
    '& thead': {
      '& th, & td': {
        backgroundColor: '$primary100',
        borderRight: '1px solid $white',
        '&:last-child': { borderRight: 0 },
      },
      '& tr:not(:first-child)': {
        '& th, & td': { borderTop: '1px solid $white' },
      },
    },
    '& tbody': {
      '& th, & td': {
        borderRight: '1px solid $gray400',
        borderBottom: '1px solid $gray400',
        '&:last-child': { borderRight: 0 },
      },
    },
  },
  variants: {
    variant: {
      lcv: {
        '& table': {
          tableLayout: 'auto',
          width: '100%',
          '& th, & td': {
            width: `${(1 / 6) * 100}%`,
          },
        },
      },
      cv: {
        '& table': {
          tableLayout: 'auto',
          width: '100%',
          '& tbody': {
            '& tr:nth-child(even)': {
              backgroundColor: '$gray300',
            },
            '& tr:nth-child(odd)': {
              backgroundColor: '$white',
            },
          },
        },
      },
    },
  },
  defaultVariants: {
    variant: 'lcv',
  },
});

const TR = styled('tr', {});

export interface TireTableProps extends PartsTireTableProps {
  dataTest?: string;
  dataTestSection?: string;
}

export const TireTable = ({
  tireTable,
  dataTest = 'partDetail',
  dataTestSection = 'section_tire_table',
}: TireTableProps) => {
  const HEADER_REF = useRef<HTMLDivElement>(null);
  const BODY_REF = useRef<HTMLDivElement>(null);

  const handleScrollHeader = (scroll) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const tableBody = BODY_REF.current as HTMLDivElement;
    if (tableBody) {
      tableBody.scrollLeft = scroll.target.scrollLeft;
    }
  };
  const handleScrollBody = (scroll) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const tableHead = HEADER_REF.current as HTMLDivElement;
    if (tableHead) {
      tableHead.scrollLeft = scroll.target.scrollLeft;
    }
  };

  let isGrayBackground = false;

  const groupedTireData = tireTable.items.reduce((acc, item) => {
    acc[item.tire] = acc[item.tire] || {};
    acc[item.tire][item.size] = acc[item.tire][item.size] || [];
    acc[item.tire][item.size].push(item);
    return acc;
  }, {});

  const hasDetailedData = tireTable.items.some(
    (item) => item.tire || item.size || item.specialPrice
  );

  return (
    <Section overflow="visible" test={dataTestSection}>
      <Container size="desktop-only">
        <Box
          marginTop={{
            '@initial': '8',
            '@sm': '10',
            '@md': '14',
          }}
          test={{
            dataTest: `${dataTest}_tire_full_table`,
          }}
        >
          {hasDetailedData ? (
            <>
              <Box position="sticky" zIndex="1" top="sticky-top">
                <Table
                  variant="lcv"
                  ref={HEADER_REF as RefObject<HTMLDivElement>}
                  onScroll={handleScrollHeader}
                >
                  <table>
                    <thead>
                      <tr>
                        <th rowSpan={2}>
                          {/* eslint-disable-next-line react/jsx-max-depth */}
                          <Text
                            variant="text-white"
                            line-height="125"
                            font="bold"
                          >
                            ขอบยาง
                          </Text>
                        </th>
                        <th rowSpan={2}>
                          {/* eslint-disable-next-line react/jsx-max-depth */}
                          <Text
                            variant="text-white"
                            line-height="125"
                            font="bold"
                          >
                            ขนาด
                          </Text>
                        </th>
                        <th rowSpan={2}>
                          {/* eslint-disable-next-line react/jsx-max-depth */}
                          <Text
                            variant="text-white"
                            line-height="125"
                            font="bold"
                          >
                            รุ่นยาง
                          </Text>
                        </th>
                        <th rowSpan={2}>
                          {/* eslint-disable-next-line react/jsx-max-depth */}
                          <Text
                            variant="text-white"
                            line-height="125"
                            font="bold"
                          >
                            ยี่ห้อ
                          </Text>
                        </th>
                        <th colSpan={2}>
                          {/* eslint-disable-next-line react/jsx-max-depth */}
                          <Text
                            variant="text-white"
                            line-height="125"
                            font="bold"
                          >
                            ราคา/เส้น
                          </Text>
                        </th>
                      </tr>
                      <tr>
                        <th>
                          {/* eslint-disable-next-line react/jsx-max-depth */}
                          <Text
                            variant="text-white"
                            line-height="125"
                            font="bold"
                          >
                            ปกติ
                          </Text>
                        </th>
                        <th>
                          <Text
                            variant="text-white"
                            line-height="125"
                            font="bold"
                          >
                            {/* eslint-disable-next-line react/jsx-max-depth */}
                            สมาชิก <br />
                            “อีซูซุ ยิ่งเข้า ยิ่งคุ้ม”
                          </Text>
                        </th>
                      </tr>
                    </thead>
                  </table>
                </Table>
              </Box>
              <Table
                variant="lcv"
                ref={BODY_REF as RefObject<HTMLDivElement>}
                onScroll={handleScrollBody}
              >
                <table>
                  <tbody>
                    {Object.entries(groupedTireData).map(
                      ([tire, sizes]: [string, { [size: string]: any[] }]) => {
                        isGrayBackground = !isGrayBackground;
                        const tireRowSpan = Object.values(sizes).flat().length;

                        return Object.entries(sizes).map(
                          ([size, items], sizeIndex) => {
                            const sizeRowSpan = items.length;

                            return items.map((item, index) => (
                              <TR
                                key={`${tire}-${size}-${index}`}
                                css={{
                                  backgroundColor: isGrayBackground
                                    ? '$white'
                                    : '$gray300',
                                }}
                              >
                                {index === 0 && sizeIndex === 0 && (
                                  <td rowSpan={tireRowSpan}>
                                    <Text
                                      variant="text-gray-darkest"
                                      size="text-base-2"
                                    >
                                      {tire}
                                    </Text>
                                  </td>
                                )}
                                {index === 0 && (
                                  <td rowSpan={sizeRowSpan}>
                                    <Text
                                      variant="text-gray-darkest"
                                      size="text-base-2"
                                    >
                                      {size}
                                    </Text>
                                  </td>
                                )}
                                <td>
                                  <Text
                                    variant="text-gray-darkest"
                                    size="text-base-2"
                                  >
                                    {item.model}
                                  </Text>
                                </td>
                                <td>
                                  <Text
                                    variant="text-gray-darkest"
                                    size="text-base-2"
                                  >
                                    {item.brand}
                                  </Text>
                                </td>
                                <td>
                                  <Text
                                    variant="text-gray-darkest"
                                    size="text-base-2"
                                  >
                                    {formatPrice(item.price, false)}
                                  </Text>
                                </td>
                                <td>
                                  <Text
                                    variant="text-red"
                                    size="text-base-2"
                                    font="bold"
                                  >
                                    {formatPrice(item.specialPrice, false)}
                                  </Text>
                                </td>
                              </TR>
                            ));
                          }
                        );
                      }
                    )}
                  </tbody>
                </table>
              </Table>
            </>
          ) : (
            <Table variant="cv">
              <table>
                <thead>
                  <tr>
                    <th>
                      <Text variant="text-white" line-height="125" font="bold">
                        รุ่นยาง
                      </Text>
                    </th>
                    <th>
                      <Text variant="text-white" line-height="125" font="bold">
                        รุ่นรถ
                      </Text>
                    </th>
                    <th>
                      <Text variant="text-white" line-height="125" font="bold">
                        ราคา
                      </Text>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tireTable.items.map((item, idx) => (
                    <tr key={idx}>
                      <td>
                        <Text
                          variant="text-gray-darkest"
                          size="text-base-2"
                          wrap
                          dangerouslySetInnerHTML={{
                            __html: item?.model ?? '',
                          }}
                        />
                      </td>
                      <td>
                        <Text
                          variant="text-gray-darkest"
                          size="text-base-2"
                          wrap
                          dangerouslySetInnerHTML={{
                            __html: item?.vehicleModel ?? '',
                          }}
                        />
                      </td>
                      <td>
                        <Text
                          variant="text-gray-darkest"
                          size="text-base-2"
                          font="bold"
                        >
                          {formatPrice(item.price, false)}
                        </Text>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Table>
          )}
        </Box>
      </Container>
    </Section>
  );
};
TireTable.displayName = 'TireTable';
